<template>
  <div class="body flex flex-col gap-y-4">
    <WInput
      v-model="contactDetails.firstName"
      type="text"
      name="firstName"
      autocomplete="given-name"
      :label="`First name${checkRequiredField('firstName') ? '*' : ''}`"
      @update:model-value="capitaliseName"
    />

    <WInput
      v-model="contactDetails.lastName"
      type="text"
      name="lastName"
      autocomplete="family-name"
      :label="`Last name${checkRequiredField('lastName') ? '*' : ''}`"
      @update:model-value="capitaliseName"
    />

    <WInput
      v-model="contactDetails.email"
      type="email"
      inputmode="email"
      name="email"
      autocmplete="email"
      :label="`Email${checkRequiredField('email') ? '*' : ''}`"
      :error="emailError"
      @update:model-value="validateEmail"
    />

    <PhoneInput
      :phone="contactDetails.phone.formatted"
      :label="`Phone${checkRequiredField('phone.formatted') ? '*' : ''}`"
      :error-message="phoneError"
      placeholder=""
      @phone-changed="phoneChange"
      @error="setError"
    />

    <p class="title3-bold mt-4">Address details</p>

    <Address @place-changed="locationChange" />
  </div>
</template>

<script setup lang="ts">
  import { watchEffect } from 'vue';
  import { Contact, FlattenObjectKeys } from 'src/types/overlays';
  import { useOverlays } from 'src/composables/overlays';
  import PhoneInput from '../PhoneInput.vue';
  import WInput from '../Input.vue';
  import Address from '../Address.vue';
  import { checkDeeplyNestedObjectProperties } from 'src/utils/helpers';

  const props = defineProps<{
    contact: Contact;
    required?: FlattenObjectKeys<Contact> | FlattenObjectKeys<Contact>[];
    accountContactPhone?: string;
  }>();

  const emit = defineEmits<{
    (e: 'error', error: boolean): void;
  }>();

  const {
    contactDetails,
    emailError,
    generalError,
    phoneError,
    capitaliseName,
    validateEmail,
    locationChange,
    phoneChange,
    setError,
  } = useOverlays(props.contact, props.accountContactPhone);

  function checkRequiredField(value: FlattenObjectKeys<Contact>) {
    if (props.required) {
      if (Array.isArray(props.required)) {
        return props.required.includes(value);
      }
      return props.required === value;
    }
    return false;
  }

  watchEffect(() => {
    if (!!emailError.value || !!phoneError.value || generalError.value) {
      emit('error', true);
      return;
    }
    if (!checkDeeplyNestedObjectProperties(contactDetails.value, props.required)) {
      emit('error', true);
      return;
    }
    emit('error', false);
  });
</script>
